import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { useTheme } from 'components/core/theme-provider';
import { useIntegrationsStore } from 'state/store/integrations/useIntegrationsStore';
import { useGetIntegrations } from 'hooks/use-get-integrations';
import { Network, ChevronDown, FileStack } from 'lucide-react';
import gdriveIcon from 'assets/images/gdrive.svg';
import onedriveIcon from 'assets/images/onedrive.svg';
import { useIsMobile } from 'hooks/use-mobile';
import { t } from 'i18next';

interface SourcesSelectorProps {
    selectedSources: string[];
    onSourcesChange: (sources: string[]) => void;
    disabled?: boolean;
    externalOpen?: boolean;
    onExternalOpenChange?: (open: boolean) => void;
}

interface SourceConfig {
    id: string;
    name: string;
    icon: string;
    color: string;
}

const sourceConfigs: Record<string, SourceConfig> = {
    'document-manager': {
        id: 'document-manager',
        name: 'DOCUMENT_MANAGEMENT_TITLE',
        icon: '',
        color: '#8B5CF6',
    },
    'google-drive': {
        id: 'google-drive',
        name: 'Google Drive',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'onedrive': {
        id: 'onedrive',
        name: 'OneDrive',
        icon: onedriveIcon,
        color: '#0078D4',
    },
    'microsoft-sharepoint': {
        id: 'microsoft-sharepoint',
        name: 'SharePoint',
        icon: '',
        color: '#0078D4',
    },
    'dropbox': {
        id: 'dropbox',
        name: 'Dropbox',
        icon: '',
        color: '#0061FF',
    },
    'gmail': {
        id: 'gmail',
        name: 'Gmail',
        icon: '',
        color: '#EA4335',
    },
    'google-calendar': {
        id: 'google-calendar',
        name: 'Google Calendar',
        icon: '',
        color: '#4285F4',
    },
    'google-contacts': {
        id: 'google-contacts',
        name: 'Google Contacts',
        icon: '',
        color: '#4285F4',
    },
    'box': {
        id: 'box',
        name: 'Box',
        icon: '',
        color: '#0061D5',
    },
};

export const SourcesSelector = ({
    selectedSources,
    onSourcesChange,
    disabled = false,
    externalOpen,
    onExternalOpenChange,
}: SourcesSelectorProps) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const { theme } = useTheme();
    const { integrations } = useIntegrationsStore();
    const { isLoading } = useGetIntegrations();
    const isMobile = useIsMobile();

    // Use external control if provided, otherwise use internal state
    const open = externalOpen !== undefined ? externalOpen : internalOpen;
    const setOpen = (value: boolean) => {
        if (onExternalOpenChange) {
            onExternalOpenChange(value);
        } else {
            setInternalOpen(value);
        }
    };

    // Default source that is always available
    const defaultSource = {
        connection_id: 'document-manager',
        integration_type: 'document-manager',
        display_name: 'Document Manager',
        status: 'active',
    };

    // Get active integrations and add default source
    const activeIntegrations = [
        defaultSource,
        ...integrations.filter((integration) => integration.status === 'active')
    ];

    const toggleSource = (sourceId: string) => {
        if (selectedSources.includes(sourceId)) {
            onSourcesChange(selectedSources.filter((id) => id !== sourceId));
        } else {
            onSourcesChange([...selectedSources, sourceId]);
        }
    };

    const getSourceConfig = (integrationType: string): SourceConfig => {
        return sourceConfigs[integrationType] || {
            id: integrationType,
            name: integrationType,
            icon: '',
            color: '#6B7280',
        };
    };

    if (isLoading) {
        return null;
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    disabled={disabled}
                    aria-pressed="false"
                    aria-label="Select sources"
                    className={
                        'relative flex h-8 min-w-8 items-center justify-center gap-1.5 rounded-full border shadow-sm hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed ' +
                        (isMobile ? 'w-8 p-2' : 'px-3 py-2') + ' ' +
                        (theme === 'dark' ? 'border-gray-500 opacity-70' : 'border-gray-200') +
                        (selectedSources.length > 0 ? ' border-primary !text-primary' : '')
                    }
                >
                    <Network className="h-4 w-4" />
                    {!isMobile && (
                        <>
                            <span className="text-[13px] whitespace-nowrap">{t('SOURCES')}</span>
                            {selectedSources.length > 0 && (
                                <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-medium">
                                    {selectedSources.length}
                                </span>
                            )}
                            <ChevronDown className="h-3 w-3" />
                        </>
                    )}
                    {isMobile && selectedSources.length > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-medium">
                            {selectedSources.length}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                className={`w-[280px] p-3 ${theme === 'dark' ? 'bg-[#1f1f1f] border-gray-700' : 'bg-white border-gray-200'}`}
                align="start"
            >
                <div className="space-y-1">
                    <div className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {t('SEARCH_CONNECTED_SOURCES')}
                    </div>
                    {activeIntegrations.map((integration) => {
                        const sourceConfig = getSourceConfig(integration.integration_type);
                        const isSelected = selectedSources.includes(integration.integration_type);

                        return (
                            <button
                                key={integration.connection_id}
                                onClick={() => toggleSource(integration.integration_type)}
                                className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-colors hover:bg-muted ${isSelected ? 'bg-muted' : ''}`}
                            >
                                <div className="flex items-center gap-3 flex-1">
                                    {sourceConfig.icon ? (
                                        <img
                                            src={sourceConfig.icon}
                                            alt={sourceConfig.name}
                                            className="w-5 h-5 flex-shrink-0"
                                        />
                                    ) : (
                                        <div
                                            className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: sourceConfig.color }}
                                        >
                                            {integration.integration_type === 'document-manager' ? (
                                                <FileStack className="h-4 w-4 text-white" />
                                            ) : (
                                                <Network className="h-3 w-3 text-white" />
                                            )}
                                        </div>
                                    )}
                                    <div className="flex flex-col items-start flex-1 min-w-0">
                                        <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                                            {t(sourceConfig.name)}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex-shrink-0">
                                    <div
                                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isSelected
                                            ? 'bg-primary border-primary'
                                            : theme === 'dark'
                                                ? 'border-gray-600'
                                                : 'border-gray-300'
                                            }`}
                                    >
                                        {isSelected && (
                                            <svg
                                                className="w-3 h-3 text-white"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
                {activeIntegrations.length === 0 && (
                    <div className={`text-sm text-center py-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('NO_CONNECTED_SOURCES_AVAILABLE')}
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};

